import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calendar } from './ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Calendar as CalendarIcon, Heart, Plus, Droplets, Zap, Moon, 
  Baby, Users, Bell, BookOpen, Smile, Frown, Meh, Activity,
  Thermometer, CloudRain, Sun, Coffee, Bed, Dumbbell, HeartPulse,
  Sparkles, Share2, Lock, Pill, ChevronRight, Check, X, Loader2
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLocale } from '../context/LocaleContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to parse date string as local date (not UTC)
// This fixes the timezone issue where "2025-12-15" was showing as Dec 14
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  // Parse YYYY-MM-DD as local date, not UTC
  const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Mood options with emojis (values are used as translation keys)
const MOOD_OPTIONS = [
  { value: 'great', emoji: '😊', color: 'bg-green-500' },
  { value: 'good', emoji: '🙂', color: 'bg-lime-500' },
  { value: 'okay', emoji: '😐', color: 'bg-yellow-500' },
  { value: 'low', emoji: '😔', color: 'bg-orange-500' },
  { value: 'bad', emoji: '😢', color: 'bg-red-500' },
];

// Symptom categories (keys used for translation)
const SYMPTOM_CATEGORIES = {
  physical: {
    key: 'physical',
    icon: Activity,
    symptoms: ['cramps', 'headache', 'fatigue', 'bloating', 'breastTenderness', 'acne', 'backPain', 'nausea', 'dizziness']
  },
  discharge: {
    key: 'discharge',
    icon: Droplets,
    symptoms: ['none', 'light', 'creamy', 'eggWhite', 'watery', 'sticky', 'spotting']
  },
  mood: {
    key: 'mood',
    icon: Heart,
    symptoms: ['happy', 'calm', 'energetic', 'moodSwings', 'irritable', 'anxious', 'sad', 'sensitive']
  },
  sleep: {
    key: 'sleep',
    icon: Moon,
    symptoms: ['goodSleep', 'insomnia', 'restless', 'oversleep', 'vividDreams', 'nightSweats']
  },
  activity: {
    key: 'activity',
    icon: Dumbbell,
    symptoms: ['exercise', 'yoga', 'walking', 'running', 'swimming', 'restDay']
  },
  intimacy: {
    key: 'intimacy',
    icon: HeartPulse,
    symptoms: ['protected', 'unprotected', 'highDrive', 'lowDrive', 'none']
  }
};

// Health articles based on cycle phase
const CYCLE_ARTICLES = {
  menstrual: [
    { title: 'Foods to Eat During Your Period', summary: 'Iron-rich foods and anti-inflammatory options to ease cramps', icon: '🥗' },
    { title: 'Gentle Exercises for Period Days', summary: 'Low-impact workouts that can help relieve discomfort', icon: '🧘' },
    { title: 'Self-Care Tips for Menstruation', summary: 'Rest, warmth, and comfort during your period', icon: '💆' },
  ],
  follicular: [
    { title: 'Energy Boost Activities', summary: 'Your energy is rising - great time for new projects', icon: '⚡' },
    { title: 'Skin Care During Follicular Phase', summary: 'Your skin may be clearer - time for exfoliation', icon: '✨' },
    { title: 'Social Activities to Try', summary: 'Rising estrogen makes this a great social time', icon: '🎉' },
  ],
  ovulation: [
    { title: 'Peak Fertility Days', summary: 'Understanding your most fertile window', icon: '🌸' },
    { title: 'High Energy Workouts', summary: 'Your body is at peak performance', icon: '💪' },
    { title: 'Communication & Confidence', summary: 'Verbal skills peak during ovulation', icon: '💬' },
  ],
  luteal: [
    { title: 'Managing PMS Symptoms', summary: 'Tips for the pre-menstrual phase', icon: '🩹' },
    { title: 'Comfort Foods That Help', summary: 'Healthy comfort food alternatives', icon: '🍫' },
    { title: 'Stress Management Techniques', summary: 'Relaxation methods for the luteal phase', icon: '🧘' },
  ]
};

export default function FloStyleWomensHealth() {
  const { toast } = useToast();
  const { t } = useLocale();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('today');
  
  // Helper function to get translated cycle articles/tips
  const getTranslatedCycleArticles = () => ({
    menstrual: [
      { title: t('womensHealth.tipMenstrualFood'), summary: t('womensHealth.tipMenstrualFoodDesc'), icon: '🥗' },
      { title: t('womensHealth.tipMenstrualExercise'), summary: t('womensHealth.tipMenstrualExerciseDesc'), icon: '🧘' },
      { title: t('womensHealth.tipMenstrualSelfCare'), summary: t('womensHealth.tipMenstrualSelfCareDesc'), icon: '💆' },
    ],
    follicular: [
      { title: t('womensHealth.tipFollicularEnergy'), summary: t('womensHealth.tipFollicularEnergyDesc'), icon: '⚡' },
      { title: t('womensHealth.tipFollicularSkin'), summary: t('womensHealth.tipFollicularSkinDesc'), icon: '✨' },
      { title: t('womensHealth.tipFollicularSocial'), summary: t('womensHealth.tipFollicularSocialDesc'), icon: '🎉' },
    ],
    ovulation: [
      { title: t('womensHealth.tipOvulationFertility'), summary: t('womensHealth.tipOvulationFertilityDesc'), icon: '🌸' },
      { title: t('womensHealth.tipOvulationWorkout'), summary: t('womensHealth.tipOvulationWorkoutDesc'), icon: '💪' },
      { title: t('womensHealth.tipOvulationCommunication'), summary: t('womensHealth.tipOvulationCommunicationDesc'), icon: '💬' },
    ],
    luteal: [
      { title: t('womensHealth.tipLutealPMS'), summary: t('womensHealth.tipLutealPMSDesc'), icon: '🩹' },
      { title: t('womensHealth.tipLutealFood'), summary: t('womensHealth.tipLutealFoodDesc'), icon: '🍫' },
      { title: t('womensHealth.tipLutealStress'), summary: t('womensHealth.tipLutealStressDesc'), icon: '🧘' },
    ]
  });
  
  // Core data
  const [prediction, setPrediction] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [dailyLogs, setDailyLogs] = useState([]);
  const [todayLog, setTodayLog] = useState(null);
  
  // Mode states
  const [isPregnancyMode, setIsPregnancyMode] = useState(false);
  const [pregnancyWeek, setPregnancyWeek] = useState(1);
  const [dueDate, setDueDate] = useState('');
  
  // UI states
  const [showLogModal, setShowLogModal] = useState(false);
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [showPregnancyModal, setShowPregnancyModal] = useState(false);
  
  // Daily log form
  const [dailyForm, setDailyForm] = useState({
    date: new Date().toISOString().split('T')[0],
    mood: '',
    energy: 5,
    symptoms: [],
    discharge: '',
    intimacy: '',
    sleep_quality: 5,
    notes: '',
    temperature: ''
  });
  
  // Helper to get local date string (YYYY-MM-DD)
  const getLocalDateString = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Period form
  const [periodForm, setPeriodForm] = useState({
    start_date: getLocalDateString(),
    end_date: '',
    flow: 'medium',
    symptoms: [],
    notes: ''
  });
  
  // Reminders
  const [reminders, setReminders] = useState([]);
  const [newReminder, setNewReminder] = useState({ type: 'pill', time: '09:00', label: '' });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      const [periodsRes, predictionRes, logsRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/health/womens-health/periods`, { credentials: 'include' }),
        fetch(`${API_URL}/api/health/womens-health/cycle-prediction`, { credentials: 'include' }),
        fetch(`${API_URL}/api/health/womens-health/daily-logs?days=30`, { credentials: 'include' }),
        fetch(`${API_URL}/api/health/womens-health/settings`, { credentials: 'include' }).catch(() => ({ ok: false }))
      ]);
      
      if (periodsRes.ok) {
        const data = await periodsRes.json();
        setPeriods(data.periods || []);
      }
      if (predictionRes.ok) {
        const data = await predictionRes.json();
        console.log('Prediction data received:', data);
        setPrediction(data);
      } else {
        console.log('Prediction API failed, status:', predictionRes.status);
      }
      if (logsRes.ok) {
        const data = await logsRes.json();
        setDailyLogs(data.logs || []);
        // Find today's log
        const today = new Date().toISOString().split('T')[0];
        const todayEntry = (data.logs || []).find(l => l.date === today);
        setTodayLog(todayEntry);
      }
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setIsPregnancyMode(data.pregnancy_mode || false);
        setDueDate(data.due_date || '');
        setReminders(data.reminders || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate cycle day and phase
  const getCycleInfo = () => {
    if (!prediction || !prediction.last_period_date) {
      return { day: 0, phase: 'unknown', totalDays: 28 };
    }
    
    const lastPeriod = new Date(prediction.last_period_date);
    const today = new Date();
    const daysSinceLastPeriod = Math.floor((today - lastPeriod) / (1000 * 60 * 60 * 24));
    const cycleLength = prediction.average_cycle_length || 28;
    const cycleDay = (daysSinceLastPeriod % cycleLength) + 1;
    
    let phase = 'follicular';
    if (cycleDay <= (prediction.average_period_length || 5)) {
      phase = 'menstrual';
    } else if (cycleDay >= 12 && cycleDay <= 16) {
      phase = 'ovulation';
    } else if (cycleDay > 16) {
      phase = 'luteal';
    }
    
    return { day: cycleDay, phase, totalDays: cycleLength };
  };

  const cycleInfo = getCycleInfo();

  // Save daily log
  const saveDailyLog = async () => {
    try {
      const response = await fetch(`${API_URL}/api/health/womens-health/daily-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(dailyForm)
      });
      
      if (response.ok) {
        toast({ title: '✅ Daily Log Saved', description: 'Your symptoms have been recorded' });
        setShowLogModal(false);
        loadAllData();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save log', variant: 'destructive' });
    }
  };

  // Save period
  const [savingPeriod, setSavingPeriod] = useState(false);
  
  const savePeriod = async () => {
    console.log('Save Period clicked, form data:', periodForm);
    
    if (!periodForm.start_date) {
      toast({ title: 'Error', description: 'Please select a start date', variant: 'destructive' });
      return;
    }
    
    setSavingPeriod(true);
    
    const requestBody = {
      start_date: periodForm.start_date,
      end_date: periodForm.end_date || null,
      flow: periodForm.flow || 'medium',
      symptoms: periodForm.symptoms || [],
      notes: periodForm.notes || ''
    };
    
    console.log('Sending request:', requestBody);
    
    try {
      const response = await fetch(`${API_URL}/api/health/womens-health/period`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Success response:', data);
        toast({ title: '✅ Period Logged', description: 'Your cycle has been updated' });
        setShowPeriodModal(false);
        setPeriodForm({
          start_date: getLocalDateString(),
          end_date: '',
          flow: 'medium',
          symptoms: [],
          notes: ''
        });
        loadAllData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error response:', errorData);
        toast({ title: 'Error', description: errorData.detail || 'Failed to log period', variant: 'destructive' });
      }
    } catch (error) {
      console.error('Error saving period:', error);
      toast({ title: 'Error', description: 'Failed to log period. Please try again.', variant: 'destructive' });
    } finally {
      setSavingPeriod(false);
    }
  };

  // Toggle pregnancy mode
  const togglePregnancyMode = async () => {
    try {
      const response = await fetch(`${API_URL}/api/health/womens-health/pregnancy-mode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          enabled: !isPregnancyMode,
          due_date: dueDate
        })
      });
      
      if (response.ok) {
        setIsPregnancyMode(!isPregnancyMode);
        toast({ 
          title: isPregnancyMode ? '🩸 Period Tracking Mode' : '🤰 Pregnancy Mode Enabled',
          description: isPregnancyMode ? 'Switched back to cycle tracking' : 'Congratulations! Tracking your pregnancy'
        });
        setShowPregnancyModal(false);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update mode', variant: 'destructive' });
    }
  };

  // Save reminder
  const saveReminder = async () => {
    try {
      const response = await fetch(`${API_URL}/api/health/womens-health/reminders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newReminder)
      });
      
      if (response.ok) {
        toast({ title: '⏰ Reminder Set', description: `${newReminder.type} reminder added` });
        setShowReminderModal(false);
        loadAllData();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save reminder', variant: 'destructive' });
    }
  };

  // Generate partner share link
  const generateShareLink = async () => {
    try {
      const response = await fetch(`${API_URL}/api/health/womens-health/share-link`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        navigator.clipboard.writeText(data.share_url);
        toast({ title: '🔗 Link Copied!', description: 'Share this link with your partner' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to generate link', variant: 'destructive' });
    }
  };

  // Toggle symptom in daily form
  const toggleDailySymptom = (symptom) => {
    setDailyForm(prev => ({
      ...prev,
      symptoms: prev.symptoms.includes(symptom)
        ? prev.symptoms.filter(s => s !== symptom)
        : [...prev.symptoms, symptom]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  // Pregnancy Mode View
  if (isPregnancyMode) {
    const weeksPregnant = dueDate ? Math.floor((40 * 7 - Math.floor((new Date(dueDate) - new Date()) / (1000 * 60 * 60 * 24))) / 7) : 0;
    const trimester = weeksPregnant <= 12 ? 1 : weeksPregnant <= 26 ? 2 : 3;
    
    return (
      <div className="space-y-6">
        {/* Pregnancy Header */}
        <div className="bg-gradient-to-r from-cyan-100 via-teal-100 to-blue-100 rounded-2xl p-6 text-center">
          <Baby className="h-16 w-16 mx-auto text-cyan-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900">Week {weeksPregnant}</h2>
          <p className="text-gray-600">Trimester {trimester}</p>
          <p className="text-sm text-gray-500 mt-2">Due: {dueDate ? new Date(dueDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Set due date'}</p>
          
          {/* Progress bar */}
          <div className="mt-4 bg-white/50 rounded-full h-3 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full transition-all"
              style={{ width: `${(weeksPregnant / 40) * 100}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">{40 - weeksPregnant} weeks to go</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3">
          <Button onClick={() => setShowLogModal(true)} className="bg-cyan-600 hover:bg-cyan-700 h-auto py-4">
            <Plus className="h-5 w-5 mr-2" />
            Log Symptoms
          </Button>
          <Button onClick={() => setShowPregnancyModal(true)} variant="outline" className="h-auto py-4">
            <X className="h-5 w-5 mr-2" />
            Exit Pregnancy Mode
          </Button>
        </div>

        {/* Pregnancy Tips */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-teal-600" />
              Week {weeksPregnant} Tips
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="p-3 bg-teal-50 rounded-lg">
              <p className="font-medium text-teal-900">Baby Development</p>
              <p className="text-sm text-teal-700">Your baby is growing! Check with your healthcare provider for detailed updates.</p>
            </div>
            <div className="p-3 bg-cyan-50 rounded-lg">
              <p className="font-medium text-pink-900">Self Care</p>
              <p className="text-sm text-cyan-700">Stay hydrated, rest when needed, and maintain regular prenatal visits.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Regular Cycle Tracking View
  return (
    <div className="space-y-6 w-full overflow-x-hidden px-1">
      {/* Cycle Wheel */}
      <div className="bg-gradient-to-br from-cyan-50 via-teal-50 to-blue-50 rounded-2xl p-4 sm:p-6 text-center relative overflow-hidden">
        <div className="relative mx-auto" style={{ width: '200px', height: '200px' }}>
          {/* Outer ring */}
          <svg className="absolute inset-0" viewBox="0 0 200 200">
            <circle cx="100" cy="100" r="90" fill="none" stroke="#e5e7eb" strokeWidth="12" />
            <circle 
              cx="100" cy="100" r="90" fill="none" 
              stroke="url(#cycleGradient)" strokeWidth="12"
              strokeDasharray={`${(cycleInfo.day / cycleInfo.totalDays) * 565} 565`}
              strokeLinecap="round"
              transform="rotate(-90 100 100)"
            />
            <defs>
              <linearGradient id="cycleGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#6EDBC8" />
                <stop offset="50%" stopColor="#4DC9C4" />
                <stop offset="100%" stopColor="#3b82f6" />
              </linearGradient>
            </defs>
          </svg>
          
          {/* Center content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {cycleInfo.phase === 'unknown' ? (
              <>
                <span className="text-2xl font-semibold text-gray-700">{t('womensHealth.welcome')} 💜</span>
                <span className="text-sm text-gray-500 mt-1">{t('womensHealth.logPeriodToStart')}</span>
              </>
            ) : (
              <>
                <span className="text-4xl font-bold text-gray-900">{t('womensHealth.cycleDay')} {cycleInfo.day}</span>
                <span className="text-sm text-gray-600 capitalize">{t(`womensHealth.${cycleInfo.phase}`)} {t('womensHealth.phase')}</span>
              </>
            )}
          </div>
        </div>
        
        {/* Phase indicators */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mt-4 text-xs px-2">
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-400 flex-shrink-0" />
            <span className="whitespace-nowrap">{t('womensHealth.menstrual')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-cyan-400 flex-shrink-0" />
            <span className="whitespace-nowrap">{t('womensHealth.follicular')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-teal-400 flex-shrink-0" />
            <span className="whitespace-nowrap">{t('womensHealth.ovulation')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-blue-400 flex-shrink-0" />
            <span className="whitespace-nowrap">{t('womensHealth.luteal')}</span>
          </div>
        </div>

        {/* Next period prediction */}
        {prediction?.next_period_date && (
          <div className="mt-4 p-3 bg-white/70 rounded-lg inline-block">
            <p className="text-sm text-gray-600">{t('womensHealth.nextPeriodIn')}</p>
            <p className="text-xl font-bold text-cyan-600">
              {Math.max(0, Math.ceil((new Date(prediction.next_period_date) - new Date()) / (1000 * 60 * 60 * 24)))} {t('womensHealth.days')}
            </p>
          </div>
        )}
      </div>

      {/* Quick Action Buttons */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 px-1">
        <Button onClick={() => setShowPeriodModal(true)} className="bg-red-500 hover:bg-red-600 h-auto py-3 sm:py-4 text-sm sm:text-base">
          <Droplets className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="truncate">{t('womensHealth.logPeriod')}</span>
        </Button>
        <Button onClick={() => setShowLogModal(true)} className="bg-cyan-600 hover:bg-cyan-700 h-auto py-3 sm:py-4 text-sm sm:text-base">
          <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="truncate">{t('womensHealth.dailyLog')}</span>
        </Button>
      </div>

      {/* Today&apos;s Mood (if logged) */}
      {todayLog && (
        <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('womensHealth.todaysMood')}</p>
                <p className="text-2xl">
                  {MOOD_OPTIONS.find(m => m.value === todayLog.mood)?.emoji || '😊'} {t(`womensHealth.${todayLog.mood}`) || t('womensHealth.none')}
                </p>
              </div>
              {todayLog.symptoms?.length > 0 && (
                <div className="text-right">
                  <p className="text-xs text-gray-500">{todayLog.symptoms.length} {t('womensHealth.symptomsLogged')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs for different sections */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-nowrap w-full overflow-x-auto gap-1 p-1">
          <TabsTrigger value="today" className="text-xs flex-shrink-0 px-2 sm:px-4">{t('womensHealth.today')}</TabsTrigger>
          <TabsTrigger value="calendar" className="text-xs flex-shrink-0 px-2 sm:px-4">{t('womensHealth.calendar')}</TabsTrigger>
          <TabsTrigger value="insights" className="text-xs flex-shrink-0 px-2 sm:px-4">{t('womensHealth.insights')}</TabsTrigger>
          <TabsTrigger value="settings" className="text-xs flex-shrink-0 px-2 sm:px-4">{t('womensHealth.settings')}</TabsTrigger>
          <TabsTrigger value="articles" className="text-xs flex-shrink-0 px-2 sm:px-4">{t('womensHealth.tips')}</TabsTrigger>
        </TabsList>

        {/* Today Tab */}
        <TabsContent value="today" className="space-y-4 mt-4">
          {/* Show helpful message if no data yet */}
          {!prediction?.last_period_date ? (
            <div className="bg-teal-50 rounded-2xl p-6 text-center border border-teal-100">
              <span className="text-4xl mb-3 block">🌸</span>
              <h3 className="font-medium text-gray-800 mb-2">{t('womensHealth.welcome')} 💜</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('womensHealth.logPeriodToStart')}
              </p>
              <p className="text-xs text-gray-500">
                {t('womensHealth.dataPrivate')}
              </p>
            </div>
          ) : (
            /* Cycle Stats - only show when we have data */
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-teal-50 rounded-xl p-4 text-center border border-teal-100">
                <Zap className="h-5 w-5 mx-auto text-teal-600 mb-2" />
                <p className="text-xs text-gray-500">{t('womensHealth.ovulation')}</p>
                <p className="font-semibold text-teal-700">
                  {prediction?.ovulation_date ? new Date(prediction.ovulation_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                </p>
              </div>
              <div className="bg-cyan-50 rounded-xl p-4 text-center border border-cyan-100">
                <Heart className="h-5 w-5 mx-auto text-cyan-600 mb-2" />
                <p className="text-xs text-gray-500">Fertile Window</p>
                <p className="font-semibold text-cyan-700">
                  {prediction?.fertile_window_start && prediction?.fertile_window_end 
                    ? `${new Date(prediction.fertile_window_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                    : '—'}
                </p>
              </div>
            </div>
          )}

          {/* Recent Logs */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Recent Logs</CardTitle>
            </CardHeader>
            <CardContent>
              {dailyLogs.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No logs yet. Start tracking today!</p>
              ) : (
                <div className="space-y-2">
                  {dailyLogs.slice(0, 5).map((log, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{MOOD_OPTIONS.find(m => m.value === log.mood)?.emoji || '📝'}</span>
                        <div>
                          <p className="text-sm font-medium">{new Date(log.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                          <p className="text-xs text-gray-500">{log.symptoms?.length || 0} symptoms</p>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-cyan-600" />
                Cycle Calendar
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Flo-style Legend */}
              <div className="mb-4 flex flex-wrap gap-4 text-xs justify-center">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                  <span>Period</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-dashed border-cyan-400 rounded-full"></div>
                  <span>Predicted Period</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-teal-400 rounded-full"></div>
                  <span>Fertile (7 days)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-dashed border-teal-500 rounded-full"></div>
                  <span>Ovulation</span>
                </div>
              </div>
              
              {/* Calendar Component with Flo-style colors */}
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={new Date()}
                  modifiers={{
                    // Logged periods (solid red) - cap at 7 days max for display
                    period: periods.flatMap(p => {
                      const dates = [];
                      const start = parseLocalDate(p.start_date);
                      if (!start) return dates;
                      // Use end_date if provided, otherwise default to 5 days
                      let end = p.end_date ? parseLocalDate(p.end_date) : new Date(start.getTime() + 4 * 24 * 60 * 60 * 1000);
                      // Cap at 7 days max for display
                      const maxEnd = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);
                      if (end > maxEnd) end = maxEnd;
                      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                        dates.push(new Date(d));
                      }
                      return dates;
                    }),
                    // Predicted period (5 days - pink dotted)
                    predictedPeriod: prediction?.next_period_date && prediction?.predicted_period_end ? 
                      (() => {
                        const dates = [];
                        const start = parseLocalDate(prediction.next_period_date);
                        const end = parseLocalDate(prediction.predicted_period_end);
                        if (!start || !end) return dates;
                        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                          dates.push(new Date(d));
                        }
                        return dates;
                      })() : [],
                    // Fertile window (~7 days - teal)
                    fertile: prediction?.fertile_window_start && prediction?.fertile_window_end ? 
                      (() => {
                        const dates = [];
                        const start = parseLocalDate(prediction.fertile_window_start);
                        const end = parseLocalDate(prediction.fertile_window_end);
                        if (!start || !end) return dates;
                        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                          dates.push(new Date(d));
                        }
                        return dates;
                      })() : [],
                    // Ovulation day (single day - teal dotted)
                    ovulation: prediction?.ovulation_date ? [parseLocalDate(prediction.ovulation_date)].filter(Boolean) : []
                  }}
                  modifiersStyles={{
                    period: { backgroundColor: '#ef4444', color: 'white', fontWeight: 'bold', borderRadius: '50%' },
                    predictedPeriod: { backgroundColor: 'transparent', border: '2px dashed #8FE8D8', color: '#db2777', borderRadius: '50%' },
                    fertile: { backgroundColor: '#2dd4bf', color: 'white', borderRadius: '50%' },
                    ovulation: { backgroundColor: 'transparent', border: '2px dashed #14b8a6', color: '#0d9488', borderRadius: '50%', fontWeight: 'bold' }
                  }}
                  className="rounded-md border"
                />
              </div>
              
              {/* Calendar Legend */}
              <div className="flex flex-wrap justify-center gap-3 mt-3 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span>Period</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full border-2 border-dashed border-cyan-400"></div>
                  <span>Predicted</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-teal-400"></div>
                  <span>Fertile</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full border-2 border-dashed border-teal-500"></div>
                  <span>Ovulation</span>
                </div>
              </div>
              
              {/* Prediction Summary */}
              {prediction && (
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                    <p className="text-teal-700 font-medium">Fertile Window</p>
                    <p className="text-teal-900">
                      {prediction.fertile_window_start && parseLocalDate(prediction.fertile_window_start)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {prediction.fertile_window_end && parseLocalDate(prediction.fertile_window_end)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                    <p className="text-teal-700 font-medium">Ovulation</p>
                    <p className="text-teal-900">
                      {prediction.ovulation_date && parseLocalDate(prediction.ovulation_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200 col-span-2">
                    <p className="text-cyan-700 font-medium">Next Period Predicted</p>
                    <p className="text-pink-900">
                      {prediction.next_period_date && parseLocalDate(prediction.next_period_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {prediction.predicted_period_end && parseLocalDate(prediction.predicted_period_end)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} (~{prediction.average_period_length || 5} days)
                    </p>
                  </div>
                </div>
              )}

              {/* Recent Periods List */}
              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 mb-3">Recent Periods</h4>
                {periods.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No periods logged yet</p>
                ) : (
                  <div className="space-y-2">
                    {periods.slice(0, 5).map((period, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                        <div>
                          <p className="font-medium text-gray-900">
                            {parseLocalDate(period.start_date)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </p>
                          <p className="text-sm text-gray-600">
                            {period.end_date ? `${Math.ceil((parseLocalDate(period.end_date) - parseLocalDate(period.start_date)) / (1000 * 60 * 60 * 24))} days` : 'Ongoing'}
                          </p>
                        </div>
                        <Badge variant="outline" className="capitalize">{period.flow} flow</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insights Tab */}
        <TabsContent value="insights" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-teal-600" />
                {t('womensHealth.cycleInsights')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 bg-cyan-50 rounded-lg">
                <p className="font-medium text-pink-900">{t('womensHealth.averageCycle')}</p>
                <p className="text-2xl font-bold text-cyan-700">{prediction?.average_cycle_length || 28} {t('womensHealth.days')}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="font-medium text-red-900">{t('womensHealth.averagePeriod')}</p>
                <p className="text-2xl font-bold text-red-700">{prediction?.average_period_length || 5} {t('womensHealth.days')}</p>
              </div>
              <div className="p-3 bg-teal-50 rounded-lg">
                <p className="font-medium text-teal-900">{t('womensHealth.cyclesTracked')}</p>
                <p className="text-2xl font-bold text-teal-700">{periods.length}</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4 mt-4">
          {/* Pregnancy Mode */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Baby className="h-6 w-6 text-cyan-500" />
                  <div>
                    <p className="font-medium">{t('womensHealth.pregnancyMode')}</p>
                    <p className="text-xs text-gray-500">{t('womensHealth.trackPregnancy')}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowPregnancyModal(true)}>
                  {isPregnancyMode ? t('notifications.disable') : t('notifications.enable')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Partner Sharing */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className="h-6 w-6 text-blue-500" />
                  <div>
                    <p className="font-medium">{t('womensHealth.partnerAccess')}</p>
                    <p className="text-xs text-gray-500">{t('womensHealth.shareCycleInfo')}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowPartnerModal(true)}>
                  <Share2 className="h-4 w-4 mr-1" />
                  {t('womensHealth.share')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Reminders */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Bell className="h-6 w-6 text-yellow-500" />
                  <div>
                    <p className="font-medium">{t('womensHealth.reminders')}</p>
                    <p className="text-xs text-gray-500">{t('womensHealth.pillPeriodAlerts')}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowReminderModal(true)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {reminders.length > 0 && (
                <div className="space-y-2">
                  {reminders.map((r, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <Pill className="h-4 w-4 text-gray-500" />
                        <span className="text-sm">{r.label || r.type}</span>
                      </div>
                      <span className="text-xs text-gray-500">{r.time}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Articles Tab */}
        <TabsContent value="articles" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-teal-600" />
                {t('womensHealth.tipsFor')} {t(`womensHealth.${cycleInfo.phase}`)} {t('womensHealth.phaseTips')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(getTranslatedCycleArticles()[cycleInfo.phase] || getTranslatedCycleArticles().follicular).map((article, idx) => (
                <div key={idx} className="p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border border-teal-100">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{article.icon}</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">{article.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{article.summary}</p>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Daily Log Modal */}
      <Dialog open={showLogModal} onOpenChange={setShowLogModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Daily Log</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Mood Selection */}
            <div>
              <Label className="text-sm font-medium">How are you feeling?</Label>
              <div className="flex justify-between mt-3">
                {MOOD_OPTIONS.map((mood) => (
                  <button
                    key={mood.value}
                    onClick={() => setDailyForm({ ...dailyForm, mood: mood.value })}
                    className={`flex flex-col items-center p-2 rounded-lg transition-all ${
                      dailyForm.mood === mood.value 
                        ? 'bg-cyan-100 ring-2 ring-cyan-500' 
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-2xl">{mood.emoji}</span>
                    <span className="text-xs mt-1">{t(`womensHealth.${mood.value}`) || mood.value}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Energy Level */}
            <div>
              <Label className="text-sm font-medium">{t('womensHealth.energyLevel') || 'Energy Level'}: {dailyForm.energy}/10</Label>
              <input
                type="range"
                min="1"
                max="10"
                value={dailyForm.energy}
                onChange={(e) => setDailyForm({ ...dailyForm, energy: parseInt(e.target.value) })}
                className="w-full mt-2 accent-cyan-500"
              />
            </div>

            {/* Symptoms by Category */}
            {Object.entries(SYMPTOM_CATEGORIES).map(([key, category]) => (
              <div key={key}>
                <Label className="text-sm font-medium flex items-center gap-2">
                  <category.icon className="h-4 w-4" />
                  {t(`womensHealth.${category.key}`) || category.key}
                </Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {category.symptoms.map((symptom) => (
                    <button
                      key={symptom}
                      onClick={() => toggleDailySymptom(symptom)}
                      className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                        dailyForm.symptoms.includes(symptom)
                          ? 'bg-cyan-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {t(`womensHealth.${symptom}`) || symptom}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* Notes */}
            <div>
              <Label className="text-sm font-medium">Notes</Label>
              <Textarea
                value={dailyForm.notes}
                onChange={(e) => setDailyForm({ ...dailyForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={3}
                className="mt-2"
              />
            </div>

            <Button onClick={saveDailyLog} className="w-full bg-cyan-600 hover:bg-cyan-700">
              <Check className="h-4 w-4 mr-2" />
              Save Daily Log
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Period Log Modal */}
      <Dialog open={showPeriodModal} onOpenChange={setShowPeriodModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Log Period</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={periodForm.start_date}
                onChange={(e) => setPeriodForm({ ...periodForm, start_date: e.target.value })}
              />
            </div>
            <div>
              <Label>End Date (Optional)</Label>
              <Input
                type="date"
                value={periodForm.end_date}
                onChange={(e) => setPeriodForm({ ...periodForm, end_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Flow Intensity</Label>
              <div className="flex gap-2 mt-2">
                {['light', 'medium', 'heavy'].map((flow) => (
                  <button
                    key={flow}
                    onClick={() => setPeriodForm({ ...periodForm, flow })}
                    className={`flex-1 py-2 rounded-lg text-sm capitalize ${
                      periodForm.flow === flow
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    {flow}
                  </button>
                ))}
              </div>
            </div>
            <Button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                console.log('Button clicked!');
                savePeriod();
              }} 
              disabled={savingPeriod} 
              className="w-full bg-red-500 hover:bg-red-600"
            >
              {savingPeriod ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Period'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pregnancy Mode Modal */}
      <Dialog open={showPregnancyModal} onOpenChange={setShowPregnancyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isPregnancyMode ? 'Disable Pregnancy Mode?' : 'Enable Pregnancy Mode'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!isPregnancyMode && (
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">Enter your estimated due date</p>
              </div>
            )}
            <p className="text-sm text-gray-600">
              {isPregnancyMode 
                ? 'This will switch back to regular period tracking.'
                : 'This will switch to pregnancy tracking mode with week-by-week updates.'}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowPregnancyModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={togglePregnancyMode} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
                {isPregnancyMode ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Partner Share Modal */}
      <Dialog open={showPartnerModal} onOpenChange={setShowPartnerModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share with Partner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Generate a secure link to share your cycle information with your partner. They&apos;ll be able to see:
            </p>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
              <li>Your current cycle phase</li>
              <li>Predicted period dates</li>
              <li>Fertile window (optional)</li>
            </ul>
            <Button onClick={generateShareLink} className="w-full bg-blue-600 hover:bg-blue-700">
              <Share2 className="h-4 w-4 mr-2" />
              Generate Share Link
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reminder Modal */}
      <Dialog open={showReminderModal} onOpenChange={setShowReminderModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Reminder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Reminder Type</Label>
              <Select value={newReminder.type} onValueChange={(v) => setNewReminder({ ...newReminder, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pill">💊 Pill Reminder</SelectItem>
                  <SelectItem value="period">🩸 Period Reminder</SelectItem>
                  <SelectItem value="fertility">💙 Fertility Window</SelectItem>
                  <SelectItem value="custom">📝 Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Time</Label>
              <Input
                type="time"
                value={newReminder.time}
                onChange={(e) => setNewReminder({ ...newReminder, time: e.target.value })}
              />
            </div>
            <div>
              <Label>Label (Optional)</Label>
              <Input
                value={newReminder.label}
                onChange={(e) => setNewReminder({ ...newReminder, label: e.target.value })}
                placeholder="e.g., Take birth control"
              />
            </div>
            <Button onClick={saveReminder} className="w-full bg-yellow-500 hover:bg-yellow-600">
              <Bell className="h-4 w-4 mr-2" />
              Save Reminder
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
